import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  useIonToast,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react'

import { Entry } from '@components/templates/entry'
import { InfiniteScrollLoader } from '@/components/atoms/InfiniteScrollLoader'
import { Loading } from '@/components/atoms/Loading'
import { TimelineError } from '@/components/organisms/TimelineError'
import { VirtualList } from '@/components/organisms/VirtualList'

import { useAccountInfo } from '@/services/account'
import { useActivity } from '@/lib/hooks/useActivity'
import { useFeed } from '@/lib/hooks/useFeed'
import { useHandleVirtuosoScrollToTop } from '@/lib/hooks/useHandleVirtuosoScrollToTop'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { useVirtualList } from '@/lib/hooks/useVirtualList'
import {
  amplitudeIdentifyEvent,
  IIdentifyEvent,
  Tracking,
  TrackingEvent,
} from '@/lib/tracking'
import { getPlatform } from '@/utils/getPlatform'
import { getPreferences } from '@/utils/preferences'

import { BranchDataParams, Pages, QueryKeys } from '@enums'

import { Header } from './Header'

export const Feed = () => {
  const [presentToast] = useIonToast()
  const [isFeedEnabled, setIsFeedEnabled] = useState(true)
  const location = useLocation()
  const { pathname = '' } = location
  const ranking = useMemo(
    () => (Math.random() > 0.5 ? 'popularity' : 'popularity_2'),
    []
  )
  const {
    feedData: timelineFeed,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetchFirstPage,
    isFetching,
    hasFeedError,
  } = useFeed({
    feedGroup: 'timeline',
    feedId: undefined,
    cacheKey: [QueryKeys.TimelineFeed],
    ranking,
  })

  const { data: accountInfoData, refetch: refetchAccountInfo } =
    useAccountInfo(undefined)
  const { data: userInfo } = useUserInfo()

  const { isAtTop, atTopStateChange, onEndReached } = useVirtualList({
    hasNextPage,
    fetchNextPage,
  })

  const {
    activeActivityId,
    handleScroll,
    renderActivity,
    scrollSeekConfigurationExit,
    scrollSeekConfigurationEnter,
  } = useActivity({
    feed: timelineFeed,
    isAtTop,
    isFeedFetching: isFetching,
    isFeedEnabled,
  })

  const { t } = useTranslation('common')
  const { virtuosoRef, scrollToTop } = useHandleVirtuosoScrollToTop()
  const handleRefetchAll = async () => {
    await Promise.all([refetchAccountInfo(), refetchFirstPage()])
  }
  // When routing changes or accountInfoData or userInfo, update user attributes
  useEffect(() => {
    if (!(userInfo && accountInfoData && pathname === `/${Pages.Feed}`)) return

    getPreferences(BranchDataParams.RefUsername).then((localRefUsername) => {
      amplitudeIdentifyEvent<IIdentifyEvent>({
        userId: userInfo.id || '-',
        followerCount: userInfo.followerCount || 0,
        followingCount: userInfo.followingCount || 0,
        userName: userInfo.username || '-',
        refUserName: userInfo.refUsername || localRefUsername || '-',
        userTotalXP: accountInfoData.xp || 0,
        isAmbassador: userInfo?.isAmbassador || false,
        badge: userInfo?.badge || '-',
        appPlatform: getPlatform(),
      })
    })
  }, [pathname, accountInfoData, userInfo])

  useEffect(() => {
    pathname === `/${Pages.Feed}` && Tracking.triggerEvent(TrackingEvent.Feed)
  }, [pathname])

  useIonViewWillLeave(() => setIsFeedEnabled(false), [])
  useIonViewWillEnter(() => setIsFeedEnabled(true), [])
  useEffect(() => setIsFeedEnabled(true), [])

  const Footer = useCallback(() => {
    return <InfiniteScrollLoader hasNext={!!isFetchingNextPage} />
  }, [isFetchingNextPage])

  const itemContent = useCallback(
    (index, item) => renderActivity(item, activeActivityId, index),
    [activeActivityId, renderActivity]
  )

  const renderFeedContent = useCallback(() => {
    if (!timelineFeed?.length) {
      if (hasFeedError) {
        return <TimelineError translation={'timeline.errors.feed'} />
      }

      if (isFetching) {
        return <Loading />
      }
    }

    return (
      <VirtualList
        atTopStateChange={atTopStateChange}
        className="h-full mx-auto max-w-[40rem]"
        data={timelineFeed}
        endReached={onEndReached}
        onScroll={handleScroll}
        overscan={300}
        components={{
          Footer,
        }}
        itemContent={itemContent}
        virtualListRef={virtuosoRef}
        scrollSeekConfiguration={{
          enter: scrollSeekConfigurationEnter,
          exit: scrollSeekConfigurationExit,
        }}
      />
    )
  }, [
    atTopStateChange,
    isFetching,
    hasFeedError,
    timelineFeed,
    virtuosoRef,
    Footer,
    itemContent,
    handleScroll,
    onEndReached,
    scrollSeekConfigurationEnter,
    scrollSeekConfigurationExit,
  ])

  const header = useMemo(
    () => (
      <Header accountInfoData={accountInfoData} scrollToTop={scrollToTop} />
    ),
    [accountInfoData, scrollToTop]
  )

  useEffect(() => {
    if (timelineFeed.length && hasFeedError) {
      presentToast({
        message: t('timeline.errors.feed'),
        position: 'top',
        duration: 3000,
      })
    }
  }, [hasFeedError, isFetching, timelineFeed, presentToast, t])

  return (
    <Entry
      headTitle={t('common.head.title.feed')}
      refetch={handleRefetchAll}
      header={header}
      isRefetchActive={isAtTop}
      onActiveNavIconPress={scrollToTop}
      hasData={!!timelineFeed?.length}
    >
      {renderFeedContent()}
    </Entry>
  )
}
